import React, { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import ApiService from "../Services/user/service";
import { useLivePrices } from "../context/LivePriceContext";

const TradingViewChart = ({ symbol, price }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const lastCandleRef = useRef(null);
  const { livePrices } = useLivePrices();
  const [interval, setInterval] = useState("15m");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch and update data
  useEffect(() => {
    if (!symbol) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        console.log(`Fetching chart data for ${symbol} with interval ${interval}`);
        const res = await ApiService.getChartData(symbol, interval);
        
        if (!isMounted) return;

        if (res.data && res.data.success) {
          const quotes = res.data.quotes || [];
          
          const formattedData = quotes
            .map((q) => {
              try {
                const t = Math.floor(new Date(q.date).getTime() / 1000);
                if (isNaN(t) || q.open === null || q.high === null || q.low === null || q.close === null) {
                  return null;
                }
                return {
                  time: t,
                  open: Number(q.open),
                  high: Number(q.high),
                  low: Number(q.low),
                  close: Number(q.close),
                };
              } catch (err) {
                return null;
              }
            })
            .filter((q) => q !== null)
            .sort((a, b) => a.time - b.time);

          // Remove duplicate timestamps (lightweight-charts throws error on duplicate time)
          const uniqueData = [];
          const seenTimes = new Set();
          for (const item of formattedData) {
            if (!seenTimes.has(item.time)) {
              seenTimes.add(item.time);
              uniqueData.push(item);
            }
          }

          if (uniqueData.length === 0) {
            setError("No historical data available for this interval");
            if (seriesRef.current) {
              seriesRef.current.setData([]);
            }
          } else {
            if (seriesRef.current) {
              seriesRef.current.setData(uniqueData);
              chartRef.current.timeScale().fitContent();
              lastCandleRef.current = { ...uniqueData[uniqueData.length - 1] };
            }
          }
        } else {
          setError(res.data?.error || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching chart data:", err);
        if (isMounted) {
          setError(err.response?.data?.error || err.message || "Failed to load chart data");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [symbol, interval]);

  // Update chart live
  useEffect(() => {
    if (!seriesRef.current || !lastCandleRef.current || !symbol) return;
    
    let currentPriceVal = null;
    if (livePrices) {
      currentPriceVal = livePrices[symbol] || livePrices[symbol.toLowerCase()];
    }
    if (!currentPriceVal && price) {
      currentPriceVal = price;
    }
    
    const p = Number(currentPriceVal);
    if (!p || isNaN(p)) return;

    // Calculate current interval timestamp
    let intervalSec = 900; // default 15m
    if (interval === "1m") intervalSec = 60;
    else if (interval === "5m") intervalSec = 300;
    else if (interval === "1h") intervalSec = 3600;
    else if (interval === "1d") intervalSec = 86400;

    // Lightweight charts expects Unix timestamp in seconds
    const nowSec = Math.floor(Date.now() / 1000);
    // Approximate interval alignment (might be slightly off for 1d due to timezone, but good enough for intraday)
    const currentIntervalTime = nowSec - (nowSec % intervalSec);

    const lastCandle = lastCandleRef.current;
    
    if (currentIntervalTime > lastCandle.time) {
      // Create a new candle for the current live time
      const newCandle = {
        time: currentIntervalTime,
        open: lastCandle.close, // open at previous close to avoid broken lines
        high: Math.max(lastCandle.close, p),
        low: Math.min(lastCandle.close, p),
        close: p
      };
      seriesRef.current.update(newCandle);
      lastCandleRef.current = newCandle;
    } else {
      // Update existing candle
      const newHigh = Math.max(lastCandle.high, p);
      const newLow = Math.min(lastCandle.low, p);
      
      const updatedCandle = {
        ...lastCandle,
        high: newHigh,
        low: newLow,
        close: p
      };

      seriesRef.current.update(updatedCandle);
      lastCandleRef.current = updatedCandle;
    }
  }, [livePrices, symbol, price, interval]);

  // Chart initialization and resize listener
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 400,
      layout: {
        background: { color: "#0b1220" },
        textColor: "#aeb2c5",
      },
      grid: {
        vertLines: { color: "rgba(255, 255, 255, 0.05)" },
        horzLines: { color: "rgba(255, 255, 255, 0.05)" },
      },
      crosshair: {
        mode: 0, // Normal crosshair
      },
      rightPriceScale: {
        autoScale: true,
        borderColor: "rgba(255, 255, 255, 0.1)",
        scaleMargins: {
          top: 0.1,    // 10% space at the top
          bottom: 0.2, // 20% space at the bottom to ensure bottom candles are fully visible
        },
      },
      timeScale: {
        borderColor: "rgba(255, 255, 255, 0.1)",
        timeVisible: true,
        secondsVisible: false,
      },
      handleScale: {
        mouseWheel: true,
        pinch: true,
        axisPressedMouseMove: {
          time: true,
          price: true, // Enables Y-axis drag scaling
        },
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
    });

    // Add candlestick series
    const candlestickSeries = chart.addCandlestickSeries({
      upColor: "#26a69a",
      downColor: "#ef5350",
      borderVisible: false,
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
    });

    chartRef.current = chart;
    seriesRef.current = candlestickSeries;

    // Double-click to reset scale
    const container = chartContainerRef.current;
    const handleDblClick = () => {
      chart.priceScale("right").applyOptions({
        autoScale: true,
      });
      chart.timeScale().fitContent();
    };
    container.addEventListener("dblclick", handleDblClick);

    // Resize handler
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      container.removeEventListener("dblclick", handleDblClick);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  const handleReset = () => {
    if (chartRef.current) {
      chartRef.current.priceScale("right").applyOptions({
        autoScale: true,
      });
      chartRef.current.timeScale().fitContent();
    }
  };

  return (
    <div style={{ background: "#0b1220", borderRadius: "12px", padding: "16px", border: "1px solid rgba(255,255,255,0.05)" }}>
      {/* Chart Header / Interval Selector */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <span className="fw-bold text-white" style={{ fontSize: "16px" }}>{symbol?.toUpperCase()}</span>
          {loading && <span className="spinner-border spinner-border-sm text-info"></span>}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            style={{
              fontSize: "11px",
              padding: "2px 8px",
              borderRadius: "4px",
              color: "#aeb2c5",
              borderColor: "rgba(255,255,255,0.1)",
              background: "transparent"
            }}
            onClick={handleReset}
          >
            Reset
          </button>
        </div>
        <div className="d-flex align-items-center gap-1 p-1 rounded-3" style={{ background: "rgba(255,255,255,0.05)" }}>
          {["1m", "5m", "15m", "1h", "1d"].map((i) => (
            <button
              key={i}
              type="button"
              className={`btn btn-sm ${interval === i ? "btn-primary shadow-sm" : "text-secondary border-0"}`}
              style={{
                fontSize: "12px",
                fontWeight: "600",
                padding: "4px 8px",
                borderRadius: "6px",
                background: interval === i ? "#3736af" : "transparent",
                color: interval === i ? "#fff" : "#aeb2c5",
                border: "none",
                transition: "all 0.2s"
              }}
              onClick={() => setInterval(i)}
            >
              {i}
            </button>
          ))}
        </div>
      </div>

      {/* Chart container */}
      <div style={{ position: "relative" }}>
        {error && (
          <div
            className="d-flex justify-content-center align-items-center text-center p-3"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              background: "rgba(11, 18, 32, 0.85)",
              color: "#ef5350",
              fontWeight: "600",
              borderRadius: "8px",
            }}
          >
            {error}
          </div>
        )}
        <div ref={chartContainerRef} style={{ width: "100%", height: "400px" }} />
      </div>
    </div>
  );
};

export default TradingViewChart;